import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { careers, getCareerBySlug, applyEmail, HIRING_PAUSED } from "../data/careers";
import useReveal from "../hooks/useReveal";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function CareerDetail() {
  useReveal();
  const { slug } = useParams();
  const item = getCareerBySlug(slug);

  // Hiring paused: old/bookmarked role links redirect to the homepage careers
  // section, which shows the paused message and CV invitation.
  if (HIRING_PAUSED) {
    return <Navigate to="/#careers" replace />;
  }

  if (!item) {
    return (
      <main className="min-h-screen bg-[#F7F5F0] text-[#1C1C19] flex flex-col">
        <Navbar />
        <section className="flex-1 flex items-center justify-center px-6 py-40">
          <div className="text-center" data-testid="career-not-found">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
              404 · Role not found
            </span>
            <h1 className="mt-6 font-display text-5xl md:text-7xl font-light tracking-tight">
              That role <span className="italic">doesn't exist.</span>
            </h1>
            <Link
              to="/#careers"
              data-testid="back-careers-link"
              className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1C1C19] text-[#F7F5F0]"
            >
              <ArrowLeft size={16} />
              All roles
            </Link>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const idx = careers.findIndex((c) => c.slug === item.slug);
  const next = careers[(idx + 1) % careers.length];
  const compose = `https://mail.google.com/mail/?view=cm&fs=1&to=${applyEmail}&su=${encodeURIComponent(
    `Application: ${item.title}`
  )}`;

  return (
    <main className="min-h-screen bg-[#F7F5F0] text-[#1C1C19] overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section
        data-testid="career-detail-hero"
        className="relative pt-32 md:pt-40 pb-24 md:pb-32"
        style={{ background: item.accent, color: item.fg }}
      >
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12">
          <Link
            to="/#careers"
            data-testid="back-to-careers"
            className="link-underline inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] opacity-80"
          >
            <ArrowLeft size={14} /> All roles
          </Link>

          <div className="mt-10 grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <span className="text-xs font-bold uppercase tracking-[0.25em] opacity-80">
                Careers · we're hiring
              </span>
              <h1
                data-testid="career-detail-title"
                className="mt-6 font-display text-5xl sm:text-6xl lg:text-[7rem] font-light tracking-tight leading-[0.95]"
              >
                {item.title}
              </h1>
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="text-xs uppercase tracking-widest border border-current/50 rounded-full px-4 py-2">
                  {item.type}
                </span>
                <span className="text-xs uppercase tracking-widest border border-current/50 rounded-full px-4 py-2">
                  {item.location}
                </span>
              </div>
            </div>
            <div className="md:col-span-4">
              <p className="text-base md:text-lg font-light leading-relaxed opacity-95">
                {item.summary}
              </p>
              <a
                href={compose}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="career-hero-apply"
                className="btn-magnetic mt-8 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#1C1C19] text-[#F7F5F0] text-sm uppercase tracking-widest"
              >
                Apply by email
                <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-3 reveal">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
              (01) · The role
            </span>
            <div className="mt-6 w-12 h-px bg-[#1C1C19]" />
          </div>
          <div className="md:col-span-9 reveal space-y-6 max-w-3xl">
            {item.about.map((para, i) => (
              <p
                key={para}
                data-testid={`career-about-${i + 1}`}
                className="text-lg md:text-xl font-light leading-relaxed text-[#1C1C19]/80"
              >
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Responsibilities + Requirements */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-6 md:gap-10">
          <div
            data-testid="career-responsibilities"
            className="reveal rounded-3xl border border-[#D8D4C7] bg-white/60 p-8 md:p-12"
          >
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight">
              What you'll do
            </h2>
            <ul className="mt-8 space-y-4">
              {item.responsibilities.map((r, i) => (
                <li
                  key={r}
                  data-testid={`career-resp-${i + 1}`}
                  className="flex items-start gap-3 text-base font-light text-[#1C1C19]/85"
                >
                  <span
                    className="mt-1 shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full"
                    style={{ background: item.accent }}
                  >
                    <Check size={12} strokeWidth={3} color="#1C1C19" />
                  </span>
                  {r}
                </li>
              ))}
            </ul>
          </div>

          <div
            data-testid="career-requirements"
            className="reveal rounded-3xl bg-[#1C1C19] text-[#F7F5F0] p-8 md:p-12"
          >
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight">
              What we're looking for
            </h2>
            <ul className="mt-8 space-y-4">
              {item.requirements.map((r, i) => (
                <li
                  key={r}
                  data-testid={`career-req-${i + 1}`}
                  className="flex items-start gap-3 text-base font-light text-[#F7F5F0]/85"
                >
                  <span
                    className="mt-1 shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full"
                    style={{ background: item.accent }}
                  >
                    <Check size={12} strokeWidth={3} color="#1C1C19" />
                  </span>
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{ background: item.accent, color: item.fg }}
      >
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-7xl font-light tracking-tight leading-[1.05]">
            Think this is <span className="italic">you?</span>
          </h2>
          <p className="mt-6 text-base md:text-lg font-light opacity-90 max-w-xl mx-auto">
            Applications are by email only. Send us your work, a short note on
            why this role, and anything you're proud of.
          </p>
          <a
            href={compose}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="career-cta-apply"
            className="btn-magnetic mt-10 inline-flex items-center gap-3 px-6 py-4 rounded-full bg-[#1C1C19] text-[#F7F5F0] text-xs sm:text-sm normal-case tracking-normal whitespace-nowrap"
          >
            {applyEmail}
            <ArrowUpRight size={16} className="shrink-0" />
          </a>
        </div>
      </section>

      {/* Next role */}
      {careers.length > 1 && (
        <section className="py-24 md:py-32">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
              Next role
            </span>
            <Link
              to={`/careers/${next.slug}`}
              data-testid="next-career-link"
              className="mt-6 group flex flex-col md:flex-row md:items-end md:justify-between gap-6"
            >
              <h3 className="font-display text-5xl md:text-8xl font-light tracking-tight leading-[1] text-[#1C1C19]">
                {next.title}
              </h3>
              <span
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full text-xs uppercase tracking-widest text-[#1C1C19]"
                style={{ background: next.accent }}
              >
                View role
                <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}
